import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { BiX, BiCalendar } from 'react-icons/bi'
import {
  synthesizeTaskIntent,
  createScheduledTask,
  updateScheduledTask,
  fetchScheduledTasks,
  selectTaskIntentSynthesis,
  selectTaskIntentStatus,
  selectTaskIntentSynthesisKey,
  selectTaskIntentSynthesisInFlightKey,
  clearTaskIntentSynthesis,
} from '../../../features/openai/openaiSlice'
import { toast } from 'react-toastify'
import { getTeamMembers } from 'actions/teamMembersActions'
import { detectBrowserTimezone } from '../../../modules/timezone'
import { shouldReplanScheduledTask, taskDefinitionSignature } from './shouldReplanScheduledTask'
import TaskScheduleFields from './TaskScheduleFields'
import TaskDeliveryFields from './TaskDeliveryFields'
import TaskConfigPlanSection from './TaskConfigPlanSection'
import ConfirmModal from './ConfirmModal'
import { buildSchedulePreview } from '../utils/schedulePreview'
import { inputClass, labelClass, sectionClass, tomorrowYmd } from './taskScheduleConstants'
import type { PlanToolStep } from '../utils/taskPlanDisplay'

function extractNotifyEmailsFromConfig(
  cfg: Record<string, any> | null | undefined,
  fallbackEmail?: string,
): string[] {
  if (!cfg || typeof cfg !== 'object') {
    return fallbackEmail ? [fallbackEmail] : []
  }
  if (Array.isArray(cfg.notify_emails) && cfg.notify_emails.length > 0) {
    return [...new Set(cfg.notify_emails.map((e: unknown) => String(e).trim().toLowerCase()).filter(Boolean))]
  }
  const legacy =
    cfg.recipient_email ||
    cfg.email?.recipient ||
    cfg.google_drive_email ||
    cfg.google_drive?.email
  if (legacy) return [String(legacy).trim().toLowerCase()]
  if (cfg.notify_on_complete === true && fallbackEmail) return [fallbackEmail]
  return []
}

const INSTRUCTIONS_HELPER_TEXT =
  'The AI reads this every time the task runs. Be specific for reports; for SEO or optimizations, describe the goal so the AI can vary products or copy each run.'

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="tw-m-0 tw-text-[10px] tw-font-semibold tw-tracking-wider tw-text-slate-400 tw-uppercase tw-mb-1.5">
      {children}
    </p>
  )
}

interface TaskConfigPanelProps {
  isOpen: boolean
  onClose: () => void
  message?: any
  agentSteps?: any[]
  /** From chat tool step; empty when creating from name/instructions only. */
  toolName?: string
  toolParameters?: Record<string, any>
  conversationHistory?: any[]
  currentChatId?: string | null
  userEmail: string
  keyId?: string
  originalMessageId?: string
  /** Edit mode: pre-fill from initialTask, do not allow changing tool/params */
  editMode?: boolean
  initialTask?: any
  /** When true, render as in-flow sidebar (no fixed overlay); matches other AI chat panels */
  inlineSidebar?: boolean
  /** Full-width body inside a bottom Sheet (mobile); no side border */
  sheetEmbedded?: boolean
  /** Show Tool pipeline section only when admin or mimic */
  isAdmin?: boolean
  isMimic?: boolean
}

export default function TaskConfigPanel({
  isOpen,
  onClose,
  message,
  agentSteps = [],
  toolName = '',
  toolParameters = {},
  conversationHistory,
  currentChatId,
  userEmail,
  keyId,
  originalMessageId,
  editMode = false,
  initialTask,
  inlineSidebar = false,
  sheetEmbedded = false,
  isAdmin = false,
  isMimic = false,
}: TaskConfigPanelProps) {
  const dispatch = useDispatch()
  const isStandaloneCreate = !editMode && (conversationHistory || []).length === 0
  const intentSynthesis = useSelector(selectTaskIntentSynthesis)
  const intentStatus = useSelector(selectTaskIntentStatus)
  const cachedSynthesisKey = useSelector(selectTaskIntentSynthesisKey)
  const inFlightSynthesisKey = useSelector(selectTaskIntentSynthesisInFlightKey)

  const clientSynthesisKey = useMemo(() => {
    if (editMode) return ''
    const chat = currentChatId ?? ''
    const msg = message?.messageId ?? ''
    const tool = toolName ?? ''
    const params = JSON.stringify(toolParameters ?? {})
    return `${chat}\u001f${msg}\u001f${tool}\u001f${params}`
  }, [editMode, currentChatId, message?.messageId, toolName, toolParameters])

  const [saving, setSaving] = useState(false)
  const [planAdjustmentText, setPlanAdjustmentText] = useState('')
  const [adjustAiExpanded, setAdjustAiExpanded] = useState(false)
  const [replanConfirmOpen, setReplanConfirmOpen] = useState(false)
  /** Instructions the current AI-generated pipeline was built for. */
  const [aiPlanSignature, setAiPlanSignature] = useState<string | null>(null)
  /** Adjustment text behind the current pipeline; sent on save so a server replan keeps its details. */
  const [appliedPlanInstructions, setAppliedPlanInstructions] = useState('')
  /** Tools pipeline from synthesize (or initialTask.tools in edit). Used for preview and create payload. */
  const [toolsPipeline, setToolsPipeline] = useState<PlanToolStep[]>([])
  const teamMembers = useSelector((store: any) => store.teamMembers?.members) as
    | Array<{ shared_with?: string; email?: string }>
    | undefined
  const teamMembersStatus = useSelector((store: any) => store.teamMembers?.fetching) as boolean | undefined
  const [selectedNotifyEmails, setSelectedNotifyEmails] = useState<string[]>(() =>
    extractNotifyEmailsFromConfig(initialTask?.delivery_config, userEmail),
  )
  const [slackDeliveryEnabled, setSlackDeliveryEnabled] = useState(() => {
    const d = initialTask?.delivery_config || {}
    return !!(d.slack_team_id && d.slack_channel_id)
  })

  const toggleNotifyEmail = useCallback((email: string) => {
    const normalized = String(email || '').trim().toLowerCase()
    if (!normalized) return
    setSelectedNotifyEmails((prev) =>
      prev.includes(normalized) ? prev.filter((e) => e !== normalized) : [...prev, normalized],
    )
  }, [])

  const onChangeStepParam = useCallback((executionOrder: number, key: string, value: string) => {
    setToolsPipeline((prev) =>
      prev.map((step, idx) => {
        const order = step.execution_order ?? idx + 1
        if (order !== executionOrder) return step
        return {
          ...step,
          tool_parameters: {
            ...(step.tool_parameters || {}),
            [key]: value,
          },
        }
      }),
    )
  }, [])

  useEffect(() => {
    if (isOpen) {
      dispatch(getTeamMembers() as any)
    }
  }, [isOpen, dispatch])

  /** Create mode only: distinguish first open vs staying open so we do not wipe the form when synthesis completes after open. */
  const createPanelWasOpenRef = useRef(false)

  const cfg = initialTask?.delivery_config || {}
  /** Legacy tasks used recipient_email / google_drive for delivery; treat as notify-on-complete for UI. */
  const legacyNotify =
    (Array.isArray(cfg.notify_emails) && cfg.notify_emails.length > 0) ||
    !!cfg.recipient_email ||
    !!cfg.email?.recipient ||
    !!cfg.google_drive_email ||
    !!cfg.google_drive?.email
  const initialNotifyOnComplete = cfg.notify_on_complete === true || legacyNotify
  const initialHasSlack = !!(cfg.slack_team_id && cfg.slack_channel_id)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: initialTask?.name ?? '',
      description: initialTask?.description ?? '',
      schedule_type: initialTask?.schedule_type ?? 'weekly',
      run_date: initialTask?.schedule_config?.run_date ?? tomorrowYmd(),
      day_of_week: initialTask?.schedule_config?.day_of_week ?? 1,
      day_of_month: initialTask?.schedule_config?.day_of_month ?? 1,
      time: initialTask?.schedule_config?.time ?? '08:00',
      timezone: initialTask?.schedule_config?.timezone ?? detectBrowserTimezone(),
      date_range_strategy: initialTask?.date_range_strategy ?? 'last_7_days',
      notify_on_complete: initialNotifyOnComplete,
    },
  })

  const watchScheduleType = watch('schedule_type')
  const watchNotifyOnComplete = watch('notify_on_complete')
  const watchName = watch('name')
  const watchDescription = watch('description')
  const watchTime = watch('time')
  const watchTimezone = watch('timezone')
  const watchDayOfWeek = watch('day_of_week')
  const watchDayOfMonth = watch('day_of_month')
  const watchRunDate = watch('run_date')
  const watchDateRangeStrategy = watch('date_range_strategy')

  const needsReplanBanner =
    !!editMode &&
    shouldReplanScheduledTask(
      { name: watchName, description: watchDescription },
      initialTask,
      { aiPlanSignature },
    )

  const preview = buildSchedulePreview({
    schedule_type: watchScheduleType,
    time: watchTime,
    timezone: watchTimezone,
    day_of_week: Number(watchDayOfWeek),
    day_of_month: Number(watchDayOfMonth),
    run_date: watchRunDate,
    date_range_strategy: watchDateRangeStrategy,
  })

  const buildDeliveryConfig = (notifyOn: boolean) => {
    const slackFields =
      slackDeliveryEnabled && initialHasSlack
        ? {
            slack_team_id: cfg.slack_team_id,
            slack_channel_id: cfg.slack_channel_id,
            ...(cfg.slack_thread_ts != null && cfg.slack_thread_ts !== ''
              ? { slack_thread_ts: cfg.slack_thread_ts }
              : {}),
          }
        : {}
    if (!notifyOn) {
      return { notify_on_complete: false, ...slackFields }
    }
    const emails = selectedNotifyEmails.length > 0
      ? selectedNotifyEmails
      : userEmail
        ? [String(userEmail).trim().toLowerCase()]
        : []
    return {
      notify_on_complete: true,
      ...(emails.length > 0 ? { notify_emails: emails } : {}),
      ...slackFields,
    }
  }

  useEffect(() => {
    if (!isOpen) {
      setAdjustAiExpanded(false)
      setReplanConfirmOpen(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen && editMode) {
      dispatch(clearTaskIntentSynthesis() as any)
    }
  }, [isOpen, editMode, initialTask?.id, dispatch])

  useEffect(() => {
    if (!isOpen || editMode !== true || intentStatus !== 'succeeded' || !intentSynthesis) return
    const name = intentSynthesis.task_name || intentSynthesis.name || ''
    const description = intentSynthesis.intent_summary || intentSynthesis.description || ''
    setValue('name', name)
    setValue('description', description)
    const dates = intentSynthesis.date_parameters || intentSynthesis.detectedDates
    if (Array.isArray(dates) && dates.length > 0 && dates[0]?.suggestedStrategy) {
      setValue('date_range_strategy', dates[0].suggestedStrategy)
    }
    const tools = intentSynthesis.tools
    if (Array.isArray(tools) && tools.length > 0) {
      setToolsPipeline(tools)
      setAiPlanSignature(taskDefinitionSignature({ name, description }))
    }
  }, [isOpen, editMode, intentSynthesis, intentStatus, setValue])

  useEffect(() => {
    if (!isOpen || editMode) return
    const history = conversationHistory || []
    if (history.length === 0) return
    const cacheHit =
      clientSynthesisKey &&
      cachedSynthesisKey === clientSynthesisKey &&
      intentStatus === 'succeeded' &&
      intentSynthesis
    const alreadyLoadingSame = clientSynthesisKey && inFlightSynthesisKey === clientSynthesisKey
    if (cacheHit || alreadyLoadingSame) {
      return
    }
    dispatch(
      synthesizeTaskIntent({
        conversationHistory: history,
        messageId: message?.messageId,
        toolName: toolName || undefined,
        toolParameters: toolParameters || undefined,
        clientSynthesisKey,
      } as any) as any
    )
    // Omit intentStatus from deps (failed → would re-dispatch). Omit intentSynthesis: use values from this render when deps change.
  }, [
    isOpen,
    editMode,
    dispatch,
    conversationHistory?.length,
    message?.messageId,
    toolName,
    toolParameters,
    clientSynthesisKey,
    cachedSynthesisKey,
    inFlightSynthesisKey,
  ])

  /** Edit: seed form from server task only when task identity / delivery config changes — not when AI refine updates intentSynthesis. */
  useEffect(() => {
    if (!isOpen || !editMode || !initialTask) return
    setValue('name', initialTask.name ?? '')
    setValue('description', initialTask.description ?? '')
    setValue('schedule_type', initialTask.schedule_type ?? 'weekly')
    setValue('run_date', initialTask.schedule_config?.run_date ?? tomorrowYmd())
    setValue('day_of_week', initialTask.schedule_config?.day_of_week ?? 1)
    setValue('day_of_month', initialTask.schedule_config?.day_of_month ?? 1)
    setValue('time', initialTask.schedule_config?.time ?? '08:00')
    setValue('timezone', initialTask.schedule_config?.timezone ?? detectBrowserTimezone())
    setValue('date_range_strategy', initialTask.date_range_strategy ?? 'last_7_days')
    setValue(
      'notify_on_complete',
      cfg.notify_on_complete === true ||
        (Array.isArray(cfg.notify_emails) && cfg.notify_emails.length > 0) ||
        !!cfg.recipient_email ||
        !!cfg.email?.recipient ||
        !!cfg.google_drive_email ||
        !!cfg.google_drive?.email
    )
    setSelectedNotifyEmails(extractNotifyEmailsFromConfig(cfg, userEmail))
    setSlackDeliveryEnabled(!!(cfg.slack_team_id && cfg.slack_channel_id))
    setAiPlanSignature(null)
    setAppliedPlanInstructions('')
    setPlanAdjustmentText('')
    if (Array.isArray(initialTask.tools) && initialTask.tools.length > 0) {
      setToolsPipeline(initialTask.tools)
    } else {
      setToolsPipeline([
        {
          tool_name: initialTask.tool_name,
          tool_parameters: initialTask.tool_parameters || {},
          execution_order: 1,
          depends_on: null,
          output_mapping: null,
        },
      ])
    }
  }, [
    isOpen,
    editMode,
    initialTask?.id,
    setValue,
    editMode && initialTask?.delivery_config ? JSON.stringify(initialTask.delivery_config) : '',
  ])

  /** Create mode: full reset on first open; while open, apply synthesis updates without wiping. */
  useEffect(() => {
    if (!isOpen || editMode) {
      if (!isOpen) createPanelWasOpenRef.current = false
      return
    }

    const justOpened = !createPanelWasOpenRef.current
    createPanelWasOpenRef.current = true

    const applyFromSynthesis = () => {
      if (!intentSynthesis) return
      setValue('name', intentSynthesis.task_name || intentSynthesis.name || '')
      setValue('description', intentSynthesis.intent_summary || intentSynthesis.description || '')
      const dates = intentSynthesis.date_parameters || intentSynthesis.detectedDates
      if (Array.isArray(dates) && dates.length > 0 && dates[0]?.suggestedStrategy) {
        setValue('date_range_strategy', dates[0].suggestedStrategy)
      }
      const tools = intentSynthesis.tools
      if (Array.isArray(tools) && tools.length > 0) {
        setToolsPipeline(tools)
      } else if (toolName) {
        setToolsPipeline([
          {
            tool_name: toolName,
            tool_parameters: toolParameters || {},
            execution_order: 1,
            depends_on: null,
            output_mapping: null,
          },
        ])
      }
    }

    const applySingleTool = () => {
      if (!toolName) return
      setToolsPipeline([
        {
          tool_name: toolName,
          tool_parameters: toolParameters || {},
          execution_order: 1,
          depends_on: null,
          output_mapping: null,
        },
      ])
    }

    if (justOpened) {
      setValue('name', '')
      setValue('description', '')
      setValue('schedule_type', 'weekly')
      setValue('run_date', tomorrowYmd())
      setValue('day_of_week', 1)
      setValue('day_of_month', 1)
      setValue('time', '08:00')
      setValue('timezone', detectBrowserTimezone())
      setValue('date_range_strategy', 'last_7_days')
      setValue('notify_on_complete', false)
      setSelectedNotifyEmails([])
      setSlackDeliveryEnabled(false)
      setToolsPipeline([])
      if (intentSynthesis) {
        applyFromSynthesis()
      } else if (toolName) {
        applySingleTool()
      }
      return
    }

    if (intentSynthesis) {
      applyFromSynthesis()
    } else if (!toolName) {
      setToolsPipeline([])
    } else {
      applySingleTool()
    }
  }, [isOpen, editMode, intentSynthesis, toolName, toolParameters, setValue])

  const standalonePlanReady = !isStandaloneCreate || toolsPipeline.length > 0

  /** Standalone: synthesize tools from name + instructions before allowing save. */
  const handleGeneratePlan = async () => {
    const v = getValues()
    const name = String(v.name || '').trim()
    const description = String(v.description || '').trim()
    if (!(description.length >= 12 || (name && description))) {
      toast.error('Add instructions describing what the task should do each run.')
      return
    }
    try {
      await dispatch(
        synthesizeTaskIntent({
          conversationHistory: [],
          messageId: message?.messageId,
          toolName: toolName || undefined,
          toolParameters: toolParameters || undefined,
          currentTaskContext: {
            name: v.name || '',
            description: v.description || '',
            schedule_type: v.schedule_type,
            date_range_strategy: v.date_range_strategy,
          },
          agentSteps: Array.isArray(agentSteps) && agentSteps.length > 0 ? agentSteps : undefined,
          refinement: false,
        } as any) as any
      ).unwrap()
    } catch (err: any) {
      toast.error(err?.error || err?.message || 'Failed to generate plan')
    }
  }

  const handleRegeneratePlan = async () => {
    const instructions = planAdjustmentText.trim()
    if (!instructions) {
      toast.error('Describe how you want the plan to change.')
      return
    }
    const v = getValues()
    const currentTaskContext = {
      name: v.name,
      description: v.description,
      schedule_type: v.schedule_type,
      date_range_strategy: v.date_range_strategy,
    }
    try {
      await dispatch(
        synthesizeTaskIntent({
          conversationHistory: conversationHistory ?? [],
          messageId: message?.messageId,
          toolName: toolName || undefined,
          toolParameters: toolParameters || undefined,
          userInstructions: instructions,
          currentTools: toolsPipeline.length > 0 ? toolsPipeline : undefined,
          currentTaskContext,
          agentSteps: Array.isArray(agentSteps) && agentSteps.length > 0 ? agentSteps : undefined,
          refinement: true,
        } as any) as any
      ).unwrap()
      setAppliedPlanInstructions(instructions)
      toast.success('Plan updated from your instructions.')
    } catch (err: any) {
      toast.error(err?.error || err?.message || 'Failed to regenerate plan')
    }
  }

  const onSubmit = async (data: any) => {
    if (editMode) {
      if (!initialTask?.id) return
      setSaving(true)
      try {
        const updatePayload: {
          id: string
          name: string
          description: string | null
          schedule_type: string
          run_date?: string
          day_of_week?: number
          day_of_month?: number
          time: string
          timezone: string
          date_range_strategy: string
          delivery_config: Record<string, any>
          tools?: typeof toolsPipeline
          replan_tools?: boolean
          revise_instructions?: string
        } = {
          id: initialTask.id,
          name: data.name || 'Scheduled Task',
          description: data.description || null,
          schedule_type: data.schedule_type,
          day_of_week: data.schedule_type === 'weekly' ? Number(data.day_of_week) : undefined,
          day_of_month: (data.schedule_type === 'monthly' || data.schedule_type === 'quarterly') ? Number(data.day_of_month) : undefined,
          time: data.time,
          timezone: data.timezone,
          date_range_strategy: data.date_range_strategy,
          delivery_config: buildDeliveryConfig(!!data.notify_on_complete),
        }
        const needsReplan = shouldReplanScheduledTask(data, initialTask, { aiPlanSignature })
        if (needsReplan) {
          // Re-plan when instructions change so newly required source tools
          // (for example read_public_google_sheet) are not left out. Name-only
          // renames skip this so a routine edit never blocks on the LLM.
          updatePayload.replan_tools = true
          // Details the user gave in Adjust with AI (a sheet URL, an account) live
          // only in that box, so the replan needs them to reproduce the plan.
          if (appliedPlanInstructions) {
            updatePayload.revise_instructions = appliedPlanInstructions
          }
        }
        if (data.schedule_type === 'once') {
          updatePayload.run_date = data.run_date
        }
        if (toolsPipeline.length > 0) {
          updatePayload.tools = toolsPipeline
        }
        await dispatch(updateScheduledTask(updatePayload) as any).unwrap()
        dispatch(fetchScheduledTasks() as any)
        toast.success(needsReplan ? 'Task and tool plan updated.' : 'Task updated.')
        onClose()
      } catch (err: any) {
        toast.error(err?.message || err?.error || 'Failed to update task')
      } finally {
        setSaving(false)
      }
      return
    }
    if (!keyId) {
      toast.error(
        isStandaloneCreate
          ? 'Agent key not found. Connect PayHelm AI in Integrations for your workspace, then try again.'
          : 'Agent key not found. Please enable Agent mode again.'
      )
      return
    }

    if (toolsPipeline.length === 0 && !String(toolName || '').trim()) {
      toast.error(
        isStandaloneCreate
          ? 'Generate a plan first so we know which tools to run.'
          : 'No tools in this task. Use Adjust with AI to regenerate a plan, or save from a chat with a completed tool step.'
      )
      return
    }

    setSaving(true)
    try {
      const payload: Record<string, any> = {
        name: data.name || 'Scheduled Task',
        description: data.description || null,
        original_message_id: originalMessageId || message?.messageId || null,
        original_chat_id: currentChatId || null,
        key_id: keyId,
        schedule_type: data.schedule_type,
        day_of_week: data.schedule_type === 'weekly' ? Number(data.day_of_week) : undefined,
        day_of_month: (data.schedule_type === 'monthly' || data.schedule_type === 'quarterly') ? Number(data.day_of_month) : undefined,
        time: data.time,
        timezone: data.timezone,
        date_range_strategy: data.date_range_strategy,
        delivery_config: buildDeliveryConfig(!!data.notify_on_complete),
      }
      if (data.schedule_type === 'once') {
        payload.run_date = data.run_date
      }
      if (toolsPipeline.length > 0) {
        payload.tools = toolsPipeline
      } else {
        payload.tool_name = toolName
        payload.tool_parameters = toolParameters || {}
      }
      const synthesizedDescription =
        intentSynthesis?.intent_summary || intentSynthesis?.description || ''
      if (
        intentSynthesis?.instruction_sections &&
        String(data.description || '').trim() === String(synthesizedDescription).trim()
      ) {
        payload.instruction_sections = intentSynthesis.instruction_sections
      }
      await dispatch(createScheduledTask(payload) as any).unwrap()
      toast.success('Scheduled task created.')
      onClose()
    } catch (err: any) {
      toast.error(err?.message || err?.error || 'Failed to create task')
    } finally {
      setSaving(false)
    }
  }

  const handleUpdateClick = () => {
    if (needsReplanBanner) {
      handleSubmit(() => setReplanConfirmOpen(true))()
      return
    }
    handleSubmit(onSubmit)()
  }

  const handleNotifyOnCompleteChange = (checked: boolean) => {
    setValue('notify_on_complete', checked)
    if (checked && selectedNotifyEmails.length === 0 && userEmail) {
      setSelectedNotifyEmails([String(userEmail).trim().toLowerCase()])
    }
    if (!checked) {
      setSelectedNotifyEmails([])
    }
  }

  if (!isOpen) return null

  return (
    <div
      className={
        sheetEmbedded
          ? 'tw-h-full tw-w-full tw-min-h-0 tw-max-w-none tw-bg-white tw-flex tw-flex-col tw-overflow-hidden'
          : inlineSidebar
          ? 'tw-h-full tw-w-full tw-min-h-0 tw-min-w-[320px] tw-max-w-2xl tw-flex-1 tw-min-w-0 tw-bg-white tw-border-l tw-border-slate-200 tw-flex tw-flex-col tw-overflow-hidden'
          : 'tw-fixed tw-top-14 tw-right-0 tw-bottom-0 tw-w-full tw-max-w-md tw-min-h-0 tw-bg-white tw-border-l tw-border-slate-200 tw-z-50 tw-flex tw-flex-col tw-overflow-hidden'
      }
      style={inlineSidebar || sheetEmbedded ? undefined : { boxShadow: '-4px 0 20px rgba(0,0,0,0.08)' }}
      role="complementary"
      aria-label="Scheduled task configuration"
    >
      {/* Header */}
      <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-bg-gradient-to-r tw-from-slate-50 tw-to-white tw-border-b tw-border-slate-200">
        <div className="tw-flex tw-items-center tw-gap-2">
          <div className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-blue-100 tw-flex tw-items-center tw-justify-center">
            <BiCalendar className="tw-w-4 tw-h-4 tw-text-blue-600" />
          </div>
          <div>
            <h3 className="tw-m-0 tw-text-base tw-font-semibold tw-text-slate-800">
              {editMode ? 'Edit task' : isStandaloneCreate ? 'New scheduled task' : 'Save as scheduled task'}
            </h3>
            <p className="tw-m-0 tw-text-xs tw-text-slate-500">
              {editMode
                ? 'Update instructions, plan, schedule, and notifications'
                : isStandaloneCreate
                  ? 'Describe the task, generate a plan, then set the schedule'
                  : 'Configure when and how to run'}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="tw-p-2 tw-rounded-lg hover:tw-bg-slate-100 tw-text-slate-500 hover:tw-text-slate-700 tw-transition-colors"
          onClick={onClose}
          aria-label="Close"
        >
          <BiX className="tw-w-5 tw-h-5" />
        </button>
      </div>

      {/* div instead of form to avoid nested <form> (parent AI index already has a form) */}
      <div className="tw-flex tw-flex-col tw-flex-1 tw-min-h-0 tw-overflow-hidden">
        <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-p-4 tw-space-y-4">
          {intentStatus === 'loading' && (
            <div className="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-bg-blue-50 tw-rounded-xl tw-border tw-border-blue-100">
              <span className="fa fa-spinner fa-spin tw-text-blue-600 tw-text-lg" />
              <div>
                <p className="tw-m-0 tw-text-sm tw-font-medium tw-text-slate-800">
                  {isStandaloneCreate ? 'Generating plan…' : 'Analyzing conversation…'}
                </p>
                <p className="tw-m-0 tw-text-xs tw-text-slate-500">
                  {isStandaloneCreate
                    ? 'Choosing tools and parameters based on your description'
                    : 'Generating task name and steps'}
                </p>
              </div>
            </div>
          )}

          <div>
            <SectionLabel>Task details</SectionLabel>
            <div className={sectionClass}>
              <div className="tw-space-y-3">
                <div>
                  <label className={labelClass} htmlFor="task-name">Task name</label>
                  <input
                    id="task-name"
                    className={`${inputClass} ${errors.name ? 'tw-border-red-300 focus:tw-ring-red-500/30 focus:tw-border-red-500' : ''}`}
                    placeholder="e.g. Top 20 Products by Revenue"
                    {...register('name', { required: true })}
                  />
                  {errors.name && <p className="tw-mt-1 tw-text-xs tw-text-red-600">Name is required</p>}
                </div>
                <div>
                  <label className={labelClass} htmlFor="task-desc">Instructions for each run</label>
                  <p className="tw-m-0 tw-mb-1 tw-text-xs tw-text-slate-500">{INSTRUCTIONS_HELPER_TEXT}</p>
                  <textarea
                    id="task-desc"
                    rows={3}
                    className={`${inputClass} tw-resize-none`}
                    placeholder="e.g. Pull last week's Google Ads spend by campaign — same breakdown each time. Or: Improve product titles for items that need SEO, focusing on different products when possible."
                    {...register('description')}
                  />
                  {editMode && (
                    <p className="tw-m-0 tw-mt-1 tw-text-[11px] tw-text-slate-500">
                      When the instructions change, AI automatically rebuilds the tool plan on save.
                    </p>
                  )}
                </div>
                <TaskConfigPlanSection
                  mode={isStandaloneCreate ? 'standalone' : 'chat_or_edit'}
                  toolsPipeline={toolsPipeline}
                  intentStatus={intentStatus}
                  intentFailed={intentStatus === 'failed'}
                  planAdjustmentText={planAdjustmentText}
                  onPlanAdjustmentTextChange={setPlanAdjustmentText}
                  adjustAiExpanded={adjustAiExpanded}
                  onToggleAdjustAi={() => setAdjustAiExpanded((v) => !v)}
                  onGeneratePlan={handleGeneratePlan}
                  onRegeneratePlan={handleRegeneratePlan}
                  onChangeStepParam={onChangeStepParam}
                  onUseExamplePrompt={(text) => setValue('description', text)}
                />
              </div>
            </div>
          </div>

          {needsReplanBanner && (
            <div className="tw-rounded-lg tw-border tw-border-amber-200 tw-bg-amber-50 tw-p-3">
              <p className="tw-m-0 tw-text-sm tw-font-medium tw-text-amber-900">
                Instructions changed
              </p>
              <p className="tw-m-0 tw-mt-0.5 tw-text-xs tw-text-amber-800">
                Saving will rebuild the tool plan so it matches these instructions.
              </p>
            </div>
          )}

          <div>
            <SectionLabel>Schedule</SectionLabel>
            <div className={sectionClass}>
              <TaskScheduleFields
                scheduleType={watchScheduleType}
                onScheduleTypeChange={(v) => setValue('schedule_type', v)}
                time={watchTime}
                onTimeChange={(v) => setValue('time', v)}
                timezone={watchTimezone}
                onTimezoneChange={(v) => setValue('timezone', v)}
                dayOfWeek={Number(watchDayOfWeek)}
                onDayOfWeekChange={(v) => setValue('day_of_week', v)}
                dayOfMonth={Number(watchDayOfMonth)}
                onDayOfMonthChange={(v) => setValue('day_of_month', v)}
                runDate={watchRunDate}
                onRunDateChange={(v) => setValue('run_date', v)}
                dateRangeStrategy={watchDateRangeStrategy}
                onDateRangeStrategyChange={(v) => setValue('date_range_strategy', v)}
                allowOnce
                showDateRange
                previewLine={preview}
              />
            </div>
          </div>

          <div>
            <SectionLabel>Delivery</SectionLabel>
            <div className={sectionClass}>
              <TaskDeliveryFields
                notifyOnComplete={!!watchNotifyOnComplete}
                onNotifyOnCompleteChange={handleNotifyOnCompleteChange}
                selectedNotifyEmails={selectedNotifyEmails}
                onToggleNotifyEmail={toggleNotifyEmail}
                userEmail={userEmail}
                teamMembers={teamMembers}
                teamMembersLoading={!!teamMembersStatus}
                slackDelivery={
                  editMode && initialHasSlack
                    ? { enabled: slackDeliveryEnabled, channelLabel: 'Slack channel' }
                    : null
                }
                onSlackDeliveryChange={setSlackDeliveryEnabled}
              />
            </div>
          </div>

          {(isAdmin || isMimic) && (
            <div>
              <SectionLabel>Technical parameters</SectionLabel>
              <div className={sectionClass}>
                {toolsPipeline.length > 0 ? (
                  <div className="tw-space-y-3">
                    {[...toolsPipeline]
                      .sort((a, b) => (a.execution_order ?? 999) - (b.execution_order ?? 999))
                      .map((step, idx) => (
                        <details
                          key={idx}
                          className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-p-3"
                        >
                          <summary className="tw-cursor-pointer tw-text-xs tw-font-medium tw-text-slate-600">
                            Step {step.execution_order ?? idx + 1} parameters (resolved at run time)
                          </summary>
                          <pre className="tw-mt-2 tw-mb-0 tw-text-xs tw-bg-slate-100 tw-rounded tw-p-2 tw-overflow-x-auto tw-max-h-24 tw-font-mono tw-text-slate-700">
                            {JSON.stringify(step.tool_parameters, null, 2)}
                          </pre>
                        </details>
                      ))}
                  </div>
                ) : (
                  <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-p-3 tw-shadow-sm">
                    <p className="tw-font-mono tw-text-sm tw-text-slate-800 tw-mb-2">{toolName || '—'}</p>
                    <p className="tw-text-xs tw-text-slate-500 tw-mb-1">Parameters (resolved at run time)</p>
                    <pre className="tw-text-xs tw-bg-slate-100 tw-rounded tw-p-2 tw-overflow-x-auto tw-max-h-32 tw-font-mono tw-text-slate-700">
                      {JSON.stringify(toolParameters || {}, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="tw-p-4 tw-bg-slate-50/80 tw-border-t tw-border-slate-200 tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between tw-flex-shrink-0">
          <div className="tw-min-w-0 tw-flex-1">
            {isStandaloneCreate && !standalonePlanReady && intentStatus !== 'loading' && (
              <p className="tw-m-0 tw-text-xs tw-text-slate-500">
                Click <span className="tw-font-medium tw-text-slate-700">Generate plan</span> above before you can save.
              </p>
            )}
            {isStandaloneCreate && intentStatus === 'loading' && (
              <p className="tw-m-0 tw-text-xs tw-text-slate-500">Generating tool plan… Save will be available when it finishes.</p>
            )}
          </div>
          <div className="tw-flex tw-gap-2 tw-justify-end tw-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-700 tw-bg-white tw-border tw-border-slate-300 tw-rounded-lg hover:tw-bg-slate-50 tw-transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saving || intentStatus === 'loading' || !standalonePlanReady}
              title={
                isStandaloneCreate && !standalonePlanReady && intentStatus !== 'loading'
                  ? 'Generate a plan first'
                  : undefined
              }
              onClick={handleUpdateClick}
              className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-blue-600 tw-rounded-lg hover:tw-bg-blue-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
            >
              {saving ? 'Saving…' : editMode ? 'Update task' : 'Save task'}
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={replanConfirmOpen}
        onClose={() => setReplanConfirmOpen(false)}
        onConfirm={() => {
          setReplanConfirmOpen(false)
          handleSubmit(onSubmit)()
        }}
        title="Rebuild tool plan?"
        message="Your instructions changed. Saving will rebuild the tool plan to match, then update the task."
        confirmText="Update task"
      />
    </div>
  )
}
